import { html, type TemplateResult } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import { ExtBaseElement } from '../../core/ExtBaseElement';
import './ext-textarea.css';

export type ExtTextareaSize = 'sm' | 'md' | 'lg';
export type ExtTextareaResize = 'none' | 'vertical' | 'horizontal' | 'both';

/**
 * <ext-textarea rows="3" resize="vertical" label="Descripcion"/>
 *
 * Light DOM. Similar a <ext-input> pero con <textarea> interno.
 *
 * Props: value, placeholder, label, required, disabled, readonly, error,
 *   hint, size, rows, resize, name, maxlength, autocomplete.
 *
 * Eventos: ext:input, ext:change, ext:blur, ext:focus (idem input).
 */
@customElement('ext-textarea')
export class ExtTextarea extends ExtBaseElement {
  @property({ type: String }) value = '';
  @property({ type: String }) placeholder = '';
  @property({ type: String }) label = '';
  @property({ type: Boolean, reflect: true }) required = false;
  @property({ type: Boolean, reflect: true }) disabled = false;
  @property({ type: Boolean, reflect: true }) readonly = false;
  @property({ type: String, reflect: true }) error = '';
  @property({ type: String }) hint = '';
  @property({ type: String, reflect: true }) size: ExtTextareaSize = 'md';
  @property({ type: Number }) rows = 3;
  @property({ type: String, reflect: true }) resize: ExtTextareaResize = 'vertical';
  @property({ type: String }) name = '';
  @property({ type: Number }) maxlength: number | null = null;
  @property({ type: String }) autocomplete = '';

  @query('textarea') private _taEl?: HTMLTextAreaElement;

  private _lastEmittedValue = '';

  protected createRenderRoot(): HTMLElement {
    return this;
  }

  connectedCallback(): void {
    super.connectedCallback();
    this._lastEmittedValue = this.value;
  }

  focus(): void {
    this._taEl?.focus();
  }

  blur(): void {
    this._taEl?.blur();
  }

  private _onInput = (ev: Event): void => {
    const v = (ev.target as HTMLTextAreaElement).value;
    this.value = v;
    this._emit('input', { value: v });
  };

  private _onChange = (ev: Event): void => {
    const v = (ev.target as HTMLTextAreaElement).value;
    this.value = v;
    if (v !== this._lastEmittedValue) {
      this._lastEmittedValue = v;
      this._emit('change', { value: v });
    }
  };

  private _onBlur = (ev: FocusEvent): void => {
    this._emit('blur', { value: (ev.target as HTMLTextAreaElement).value });
  };

  private _onFocus = (): void => {
    this._emit('focus');
  };

  render(): TemplateResult {
    const hasError = !!this.error;
    const helperText = hasError ? this.error : this.hint;

    return html`
      ${this.label
        ? html`<label class="ext-textarea__label">
            ${this.label}${this.required ? html`<span class="ext-textarea__req" aria-hidden="true">*</span>` : null}
          </label>`
        : null}
      <div class="ext-textarea__wrapper">
        <textarea
          class="ext-textarea__field"
          .value=${this.value}
          placeholder=${this.placeholder || ''}
          name=${this.name || ''}
          rows=${this.rows}
          ?required=${this.required}
          ?disabled=${this.disabled}
          ?readonly=${this.readonly}
          maxlength=${this.maxlength ?? ''}
          autocomplete=${this.autocomplete || ''}
          aria-invalid=${hasError ? 'true' : 'false'}
          @input=${this._onInput}
          @change=${this._onChange}
          @blur=${this._onBlur}
          @focus=${this._onFocus}
        ></textarea>
      </div>
      ${helperText
        ? html`<div class="ext-textarea__helper ${hasError ? 'is-error' : ''}">${helperText}</div>`
        : null}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'ext-textarea': ExtTextarea;
  }
}
